import { TextareaHTMLAttributes, forwardRef } from 'react';
import './Form.css';

export type TextareaSize = 'sm' | 'md' | 'lg';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Size variant */
  size?: TextareaSize;
  /** Error state */
  error?: boolean;
  /** Full width textarea */
  fullWidth?: boolean;
  /** Enable resize */
  resize?: 'none' | 'vertical' | 'horizontal' | 'both';
}

/**
 * Textarea component for multi-line text entry.
 *
 * @example
 * ```tsx
 * <Textarea
 *   placeholder="Enter description..."
 *   value={description}
 *   onChange={(e) => setDescription(e.target.value)}
 *   rows={4}
 * />
 * ```
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      size = 'md',
      error = false,
      fullWidth = false,
      resize = 'vertical',
      className = '',
      style,
      ...props
    },
    ref
  ) => {
    const classNames = [
      'ui-textarea',
      `ui-textarea--${size}`,
      error && 'ui-textarea--error',
      fullWidth && 'ui-textarea--full-width',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <textarea
        ref={ref}
        className={classNames}
        style={{ ...style, resize }}
        {...props}
      />
    );
  }
);

Textarea.displayName = 'Textarea';
